import type { ApolloContextValue } from '@apollo/client/react';
import type { PureFunction } from '@bonsai-components/utility-types';
import { type Document, parseAllDocuments, stringify } from 'yaml';
import {
  type BuilderRecipeDetailsFragment,
  InstallRecipesForCurrentUserDocument,
  type InstallRecipesForCurrentUserMutation,
  type InstallRecipesForCurrentUserMutationVariables,
  isRecipeDetailARecipeDetailError,
  isRecipeDetailARecipeDetailLoading,
  isRecipeInstallationARecipeInstallationError,
  isRecipeInstallationARecipeInstallationFinished,
  RecipeInstallationStatusDocument,
  type RecipeInstallationStatusQuery,
  type RecipeInstallationStatusQueryVariables
} from '../../__generated__/apollo-hooks';
import { transformRecipeToNodesAndLinks } from '../../components/recipe-topology-viewer/recipe-topology.helper';
import { GRAPHQL_POLLING_INTERVAL } from '../../constants/general';
import { mapRecipeDetailsFragmentToDisplay } from '../../helpers/recipe-builder.helpers';
import { toBase64 } from '../encoding.helper';
import { dataOrThrow } from '../graphql-error.helper';
import type { GraphLink, GraphNodeData } from '../indexedDb.helper';

const INSTALLATION_POLL_TIMEOUT_MS = 120000;

type ParsedYamlDocAsJSON = {
  type: string;
  name: string;
  displayName: string;
  description: string;
  tags?: string[];
  recipeList: ParsedYamlRecipeListItem[];
  preconditions?: ParsedYamlRecipeListItem[];
};

type ParsedYamlRecipeListItem =
  | string
  | {
      [key: string]: {
        [key: string]: string | string[] | boolean | number | null | undefined;
      };
    };

/**
 * The parsed yaml will look something like this:
 * {
 *   "type": "specs.openrewrite.org/v1beta/recipe",
 *   "name": "com.myuser.recipes.MyRecipe",
 *   "displayName": "My custom recipe",
 *   "description": "Fix all the things.",
 *   "recipeList": [
 *     {
 *       "org.openrewrite.java.ReorderMethodArguments": {
 *         "methodPattern": "org.junit.Assume assume*(String, boolean)",
 *         "newParameterNames": "b,message",
 *         "oldParameterNames": "message,b"
 *       }
 *     },
 *   "org.openrewrite.java.testing.junit5.IgnoreToDisabled"
 * }
 */

/**
 * Round-trips a YAML composite through the v2 install pipeline using the
 * same pattern as `useInstallAndRunBuilderRecipe`:
 *   1. Install the YAML via `installRecipesForCurrentUser` (no org arg —
 *      backend routes to the current user).
 *   2. If not synchronously Finished, poll `recipeInstallationStatus`
 *      against the currently-selected org's installations connection
 *      (the install record surfaces there).
 *   3. Read back the resulting recipe descriptor from the installation
 *      envelope and transform its `detail.graph` into nodes/links.
 *
 * The recipe descriptor (with `detail.graph`) is included inline on the
 * mutation/status response via `recipes { ...BuilderRecipeDetails }`, so
 * no separate `BuilderRecipeDetailDocument` query is needed.
 */
export const transformMultipleYamlDocumentsToBuilderV2Recipe: PureFunction<
  {
    yamlString: string;
    parsedYamlDocuments: Document.Parsed[];
    apolloContext: ApolloContextValue;
    organizationId: string;
  },
  Promise<{
    name: string;
    id: string;
    nodes: GraphNodeData[];
    links: GraphLink[];
  }>
> = async ({
  yamlString,
  parsedYamlDocuments,
  apolloContext,
  organizationId
}) => {
  const recipes = parsedYamlDocuments.map((doc) => doc.toJSON());
  const rootRecipe = findRootRecipe(recipes);
  if (!rootRecipe?.name) {
    throw new Error('No root recipe found in YAML.');
  }
  if (!organizationId) {
    throw new Error(
      'No organization selected; cannot poll the installation status.'
    );
  }

  const installedRecipes = await installYamlAndCollectRecipes(
    yamlString,
    rootRecipe.name,
    organizationId,
    apolloContext
  );

  return composeBuilderRecipe(rootRecipe, recipes, installedRecipes);
};

/**
 * Like {@link transformMultipleYamlDocumentsToBuilderV2Recipe}, but for a
 * bundle that is already installed: each YAML doc's descriptor is read with
 * `fetchRecipe` instead of re-installing the bundle.
 */
export const transformInstalledYamlToBuilderV2Recipe: PureFunction<
  {
    yamlString: string;
    rootRecipeId: string;
    fetchRecipe: (
      recipeId: string
    ) => Promise<BuilderRecipeDetailsFragment | undefined>;
  },
  Promise<{
    name: string;
    id: string;
    nodes: GraphNodeData[];
    links: GraphLink[];
  }>
> = async ({ yamlString, rootRecipeId, fetchRecipe }) => {
  const recipes = parseAllDocuments(yamlString).map((doc) => doc.toJSON());
  const rootRecipe = recipes.find((recipe) => recipe.name === rootRecipeId);
  if (!rootRecipe) {
    throw new Error(`Recipe "${rootRecipeId}" not found in its YAML bundle.`);
  }

  // Sequential: concurrent executes of one lazy query supersede each other.
  const installedRecipes: BuilderRecipeDetailsFragment[] = [];
  for (const recipe of recipes) {
    const fetched = await fetchRecipe(recipe.name);
    if (fetched) {
      installedRecipes.push(fetched);
    }
  }
  throwIfAnyRecipeDetailErrored(installedRecipes);

  return composeBuilderRecipe(rootRecipe, recipes, installedRecipes);
};

const composeBuilderRecipe = (
  rootRecipe: ParsedYamlDocAsJSON,
  recipes: ParsedYamlDocAsJSON[],
  installedRecipes: BuilderRecipeDetailsFragment[]
) => {
  const { nodes, links } = composeRecipeFromInstallResponse(
    rootRecipe,
    recipes,
    installedRecipes
  );

  if (!nodes.length) {
    throw new Error(
      `Recipe "${rootRecipe.name}" installed but came back with an empty graph. No changes have been saved.`
    );
  }

  return {
    name: rootRecipe.displayName,
    id: rootRecipe.name,
    nodes,
    links
  };
};

/**
 * Stitches the install response's per-recipe graphs together using the
 * YAML structure as the composition spec. For each YAML doc we splice in
 * the matching installed recipe's `detail.graph` (its root vertex + the
 * children the backend resolved), then recurse for any cross-YAML
 * references in the YAML's recipeList/preconditions that the backend
 * didn't expand inline (the saas resolver chain currently doesn't see
 * across YAML bundle boundaries — see saas#865 follow-up).
 *
 * Once the resolver-chain gap is fully closed on the backend, the
 * cross-YAML recursion becomes a no-op (alreadyPresent check trips for
 * every reference) and this function collapses to a single
 * `transformGraphToNodesAndLinks` call.
 */
const composeRecipeFromInstallResponse = (
  rootDoc: ParsedYamlDocAsJSON,
  yamlDocs: ParsedYamlDocAsJSON[],
  installedRecipes: BuilderRecipeDetailsFragment[]
): { nodes: GraphNodeData[]; links: GraphLink[] } => {
  const recipesById = new Map(installedRecipes.map((r) => [r.id, r]));
  // Stable handles for the rename case: the YAML's `displayName` matches
  // either `name` (the alias of `displayName` in `BuilderRecipeDetails`) or
  // `instanceName` (typically equal to displayName when only one instance
  // exists). First registration wins so we don't accidentally collide
  // multiple recipes onto the same descriptive name.
  const recipesByDisplayName = new Map<string, BuilderRecipeDetailsFragment>();
  for (const r of installedRecipes) {
    if (r.name && !recipesByDisplayName.has(r.name)) {
      recipesByDisplayName.set(r.name, r);
    }
    if (r.instanceName && !recipesByDisplayName.has(r.instanceName)) {
      recipesByDisplayName.set(r.instanceName, r);
    }
  }
  const yamlDocsByName = new Map(yamlDocs.map((d) => [d.name, d]));

  const lookupInstalled = (
    doc: ParsedYamlDocAsJSON
  ): BuilderRecipeDetailsFragment | undefined => {
    const byId = recipesById.get(doc.name);
    if (byId) {
      return byId;
    }
    // Fall back to matching by displayName. Handles the case where the
    // backend renames recipe ids on re-install (e.g. an existing id is
    // already claimed by the previous install of the same YAML).
    return doc.displayName
      ? recipesByDisplayName.get(doc.displayName)
      : undefined;
  };

  const nodes: GraphNodeData[] = [];
  const links: GraphLink[] = [];
  let nextId = 0;
  // doc.name -> the new node id we emitted for that doc's root, so a
  // second-parent reference (diamond shape) can still emit its edge to
  // the existing subgraph instead of being silently dropped on dedup.
  const splicedYamlDocs = new Map<string, number>();
  const missingDocs: { name: string; displayName: string }[] = [];

  const processDoc = (
    doc: ParsedYamlDocAsJSON,
    parentNewId: number | undefined,
    isPrecondition: boolean
  ): void => {
    const existingRootNewId = splicedYamlDocs.get(doc.name);
    if (existingRootNewId !== undefined) {
      // Already spliced — still emit the parent edge so a diamond
      // reference (two parents referencing the same doc) retains the
      // second edge. Without this the second parent's subtree would be
      // orphaned and dropped on YAML round-trip.
      if (parentNewId !== undefined) {
        links.push({ source: parentNewId, target: existingRootNewId });
      }
      return;
    }

    const installed = lookupInstalled(doc);
    if (!installed) {
      // Collect rather than throw immediately so we can report every miss
      // in one error. Silently dropping would persist a partial recipe
      // with branches missing — strictly worse than failing the save.
      missingDocs.push({
        name: doc.name,
        displayName: doc.displayName ?? ''
      });
      return;
    }

    const subgraph = transformRecipeToNodesAndLinks(
      installed,
      nextId,
      isPrecondition
    );
    if (!subgraph.nodes.length) {
      return;
    }

    const docRootNewId = subgraph.nodes[0].id;
    splicedYamlDocs.set(doc.name, docRootNewId);
    // Mark every vertex whose descriptor id matches a YAML doc in the
    // bundle as `isCustomRecipe: true`. Two cases this catches:
    //   1. The subgraph's own root (always — we walked it because the
    //      user authored the YAML doc that produced it).
    //   2. Nested vertices when the backend has already expanded
    //      cross-YAML references inline (saas#865 partial fix): the inner
    //      custom recipe shows up as a non-root descendant of the outer's
    //      graph, and `transformGraphToNodesAndLinks` only flags the root
    //      vertex.
    // Without this, the YAML emit on round-trip drops nested YAML docs
    // because `createYamlFromNodesAndLinks` only walks `isCustomRecipe`
    // nodes.
    for (let i = 0; i < subgraph.nodes.length; i++) {
      const node = subgraph.nodes[i];
      const recipeId = node.val?.id;
      if (i === 0 || (recipeId && yamlDocsByName.has(recipeId))) {
        subgraph.nodes[i] = { ...node, isCustomRecipe: true };
      }
    }
    nodes.push(...subgraph.nodes);
    links.push(...subgraph.links);
    nextId += subgraph.nodes.length;

    if (parentNewId !== undefined) {
      links.push({ source: parentNewId, target: docRootNewId });
    }

    // For each YAML ref the backend didn't expand inline (cross-YAML
    // boundary), splice the referenced doc's graph in here.
    const handleRef = (
      ref: ParsedYamlRecipeListItem,
      childIsPrecondition: boolean
    ): void => {
      const refId = typeof ref === 'string' ? ref : Object.keys(ref)[0];
      const childYamlDoc = yamlDocsByName.get(refId);
      if (!childYamlDoc) {
        return;
      }
      const alreadyPresent = subgraph.links.some((link) => {
        if (link.source !== docRootNewId) {
          return false;
        }
        const childNode = subgraph.nodes.find((n) => n.id === link.target);
        return childNode?.val?.id === refId;
      });
      if (alreadyPresent) {
        return;
      }
      processDoc(childYamlDoc, docRootNewId, childIsPrecondition);
    };

    for (const p of doc.preconditions ?? []) {
      handleRef(p, true);
    }
    for (const r of doc.recipeList ?? []) {
      handleRef(r, false);
    }
  };

  processDoc(rootDoc, undefined, false);

  if (missingDocs.length > 0) {
    const missingList = missingDocs
      .map(({ name, displayName }) =>
        displayName ? `"${name}" (displayName "${displayName}")` : `"${name}"`
      )
      .join(', ');
    const installedIds = installedRecipes.map((r) => r.id).join(', ');
    throw new Error(
      `Installation completed but ${missingDocs.length} YAML doc(s) were not found in the install response: ${missingList}. ` +
        'The backend may have renamed these recipe ids during install. ' +
        `Installed recipe ids: ${installedIds}`
    );
  }

  return { nodes, links };
};

const installYamlAndCollectRecipes = async (
  yamlString: string,
  primary: string,
  organizationId: string,
  apolloContext: ApolloContextValue
): Promise<BuilderRecipeDetailsFragment[]> => {
  const installResult = await apolloContext.client.mutate<
    InstallRecipesForCurrentUserMutation,
    InstallRecipesForCurrentUserMutationVariables
  >({
    mutation: InstallRecipesForCurrentUserDocument,
    variables: {
      bundle: { yaml: { yaml: toBase64(yamlString), primary } }
    }
  });

  const installation = installResult.data?.installRecipesForCurrentUser;
  if (!installation) {
    throw new Error('Failed to start recipe installation.');
  }
  if (isRecipeInstallationARecipeInstallationError(installation)) {
    throw new Error(installation.message || 'Recipe installation failed.');
  }

  // Sync-finished installs can still have recipes whose `detail` state
  // machine is RecipeDetailLoading. Always pass through the poll loop so
  // we don't return mid-resolution.
  const installationId = installation.id;
  if (
    isRecipeInstallationARecipeInstallationFinished(installation) &&
    allRecipeDetailsSettled(installation.recipes ?? [])
  ) {
    const recipes = installation.recipes ?? [];
    throwIfAnyRecipeDetailErrored(recipes);
    return recipes;
  }

  return pollInstallationAndDetailUntilFinished(
    installationId,
    organizationId,
    apolloContext
  );
};

/**
 * Thrown when a recipe was successfully installed to the marketplace but the
 * backend could not resolve its detail graph (e.g. a referenced Maven artifact
 * is unavailable). The import is a partial success: the recipe can be run from
 * the marketplace but cannot be displayed in the visual Builder graph.
 */
export class RecipeDetailResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecipeDetailResolutionError';
    // Ensures instanceof works correctly across all bundler transpilation paths.
    Object.setPrototypeOf(this, RecipeDetailResolutionError.prototype);
  }
}

/**
 * Throws if any recipe's `detail` state machine landed in
 * `RecipeDetailError`. Called from both the sync-finished install path
 * and the polling path so the user gets the actual backend error message
 * rather than a generic downstream "empty graph" failure.
 *
 * Throws {@link RecipeDetailResolutionError} (not a plain Error) so callers
 * can distinguish "installed but graph unavailable" from "install failed".
 */
const throwIfAnyRecipeDetailErrored = (
  recipes: BuilderRecipeDetailsFragment[]
): void => {
  const errored = recipes.find(
    (r) => r.detail != null && isRecipeDetailARecipeDetailError(r.detail)
  );
  if (errored) {
    throw new RecipeDetailResolutionError(
      `Failed to resolve recipe details: ${(errored.detail as { message?: string }).message || 'Unknown error'}`
    );
  }
};

/**
 * Polls `recipeInstallationStatus` until BOTH the installation reaches
 * `RecipeInstallationFinished` AND every recipe's `detail` state machine
 * has transitioned out of `RecipeDetailLoading`. The two state machines
 * run independently on the backend — install completing doesn't imply
 * the per-recipe descriptor/graph computation is done.
 */
const pollInstallationAndDetailUntilFinished = async (
  installationId: string,
  organizationId: string,
  apolloContext: ApolloContextValue
): Promise<BuilderRecipeDetailsFragment[]> => {
  const startTime = Date.now();

  while (true) {
    if (Date.now() - startTime > INSTALLATION_POLL_TIMEOUT_MS) {
      throw new Error('Recipe installation timed out. Please try again.');
    }

    await new Promise((resolve) =>
      setTimeout(resolve, GRAPHQL_POLLING_INTERVAL)
    );

    const data = await dataOrThrow(
      apolloContext.client.query<
        RecipeInstallationStatusQuery,
        RecipeInstallationStatusQueryVariables
      >({
        query: RecipeInstallationStatusDocument,
        variables: { organizationId, installationId },
        fetchPolicy: 'network-only'
      })
    );

    const node =
      data.organization?.marketplace?.installations?.edges?.at(0)?.node;
    if (!node) {
      throw new Error('Installation not found.');
    }
    if (isRecipeInstallationARecipeInstallationError(node)) {
      throw new Error(node.message || 'Recipe installation failed.');
    }
    if (
      isRecipeInstallationARecipeInstallationFinished(node) &&
      allRecipeDetailsSettled(node.recipes ?? [])
    ) {
      const recipes = node.recipes ?? [];
      throwIfAnyRecipeDetailErrored(recipes);
      return recipes;
    }
  }
};

// A recipe is considered settled only when `detail` is present AND has
// transitioned out of `RecipeDetailLoading`. Treating a missing/null
// detail as settled would stop polling on a transient state where the
// backend hasn't yet emitted the state-machine envelope.
const allRecipeDetailsSettled = (
  recipes: BuilderRecipeDetailsFragment[]
): boolean =>
  recipes.every(
    (r) => Boolean(r.detail) && !isRecipeDetailARecipeDetailLoading(r.detail)
  );

const findRootRecipe = (
  recipes: ParsedYamlDocAsJSON[]
): ParsedYamlDocAsJSON => {
  const allDependencies = new Set<string>();
  recipes.forEach((recipe) => {
    recipe?.recipeList?.forEach((dependency) => {
      if (typeof dependency === 'string') {
        allDependencies.add(dependency);
      } else {
        allDependencies.add(Object.keys(dependency)[0]);
      }
    });
    recipe?.preconditions?.forEach((precondition) => {
      if (typeof precondition === 'string') {
        allDependencies.add(precondition);
      } else {
        allDependencies.add(Object.keys(precondition)[0]);
      }
    });
  });
  return recipes.find((recipe) => !allDependencies.has(recipe.name));
};

const extractParsingErrorsFromYamlDocuments = (
  parsedYamlDocuments: Document.Parsed[]
): Error[] => {
  const errors: Error[] = [];

  for (const doc of parsedYamlDocuments) {
    if (doc.errors.length > 0) {
      doc.errors.forEach((error) => {
        errors.push(new Error(error.message));
      });
    }
  }

  return errors;
};

export const extractParsingErrorsAsString = (
  parsedYamlDocuments: Document.Parsed[]
): string => {
  const errors = extractParsingErrorsFromYamlDocuments(parsedYamlDocuments);
  return errors.map((error) => error.message).join('\n');
};

export type CreateYamlOptions = {
  /**
   * INTENT: we need to exclude the custom descriptions for more accurate AI
   * description generation
   */
  excludeCustomRecipeDescriptions?: boolean;
};

/**
 * Creates YAML for running a single recipe node from within a composite.
 * For marketplace recipes, generates a wrapper recipe containing just that recipe.
 * For custom/nested recipes, extracts the subtree and generates full YAML.
 */
export const createYamlForSingleNode = (
  node: GraphNodeData,
  allNodes: GraphNodeData[],
  allLinks: GraphLink[]
): string => {
  // For non-custom (marketplace) recipes, wrap in a simple composite
  if (!node.isCustomRecipe) {
    const recipeEntry = mapRecipeDetailsFragmentToDisplay(node.val);
    const doc = {
      type: 'specs.openrewrite.org/v1beta/recipe',
      name: `run.single.${node.val?.id || 'recipe'}`,
      displayName: node.name || node.val?.name || 'Single Recipe Run',
      description: `Running single recipe: ${node.name || node.val?.name}`,
      recipeList: [recipeEntry]
    };
    return stringify(doc);
  }

  // For custom recipes, extract the subtree (node + all descendants)
  const descendantIds = new Set<number>([node.id]);
  const collectDescendants = (nodeId: number) => {
    allLinks
      .filter((link) => link.source === nodeId)
      .forEach((link) => {
        descendantIds.add(link.target);
        collectDescendants(link.target);
      });
  };
  collectDescendants(node.id);

  // Filter nodes and links to only include the subtree
  const subtreeNodes = allNodes.filter((n) => descendantIds.has(n.id));
  const subtreeLinks = allLinks.filter(
    (link) => descendantIds.has(link.source) && descendantIds.has(link.target)
  );

  return createYamlFromNodesAndLinks(subtreeNodes, subtreeLinks);
};

const createYamlDefaults = {
  excludeCustomRecipeDescriptions: false
};

export const createYamlFromNodesAndLinks = (
  nodes: GraphNodeData[],
  links: GraphLink[],
  options: CreateYamlOptions = {}
): string => {
  const { excludeCustomRecipeDescriptions } = {
    ...createYamlDefaults,
    ...options
  };
  const nodeMap: { [key: number]: GraphNodeData } = {};
  const childrenMap: { [key: number]: number[] } = {};

  nodes?.forEach((node) => {
    nodeMap[node.id] = node;
    childrenMap[node.id] = [];
  });

  links?.forEach((link) => {
    childrenMap[link.source].push(link.target);
  });

  function createYamlDocuments(): string[] {
    const documents: string[] = [];
    nodes?.forEach((node) => {
      const hasChildren = childrenMap[node.id].length > 0;
      if (node.isCustomRecipe && (hasChildren || node.id === 0)) {
        const recipeList: ReturnType<
          typeof mapRecipeDetailsFragmentToDisplay
        >[] = [];
        const preconditions: ReturnType<
          typeof mapRecipeDetailsFragmentToDisplay
        >[] = [];

        // single pass to sort into recipeList and preconditions lists
        childrenMap[node.id].forEach((childId) => {
          const childNode = nodeMap[childId];
          if (childNode?.isPrecondition) {
            preconditions.push(
              mapRecipeDetailsFragmentToDisplay(childNode?.val)
            );
          } else {
            recipeList.push(mapRecipeDetailsFragmentToDisplay(childNode?.val));
          }
        });

        const doc = {
          type: 'specs.openrewrite.org/v1beta/recipe',
          name: node?.val?.id,
          displayName: node?.val?.name,
          description: node?.val?.description ?? '',
          preconditions: preconditions.length > 0 ? preconditions : undefined,
          recipeList
        };

        if (excludeCustomRecipeDescriptions && node.isCustomRecipe) {
          doc.description = '';
        }

        documents.push(stringify(doc));
      }
    });
    return documents;
  }

  const yamlDocuments = createYamlDocuments();
  return yamlDocuments.join('---\n');
};
